import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Phone, Lock, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { useFirebaseOTP } from "@/hooks/useFirebaseOTP";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_097dcc50-4812-4b75-a884-fb87f03fa7a9/artifacts/6cfgy4bw_logo%20%281%29.webp";

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1=mobile 2=otp 3=new-password
  const [mobile, setMobile] = useState("");
  const [otpInput, setOtpInput] = useState("");
  const [resetToken, setResetToken] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const otp = useFirebaseOTP();

  const handleSendOTP = async () => {
    const ok = await otp.sendOTP(mobile);
    if (ok) setStep(2);
  };

  const handleVerifyOTP = async () => {
    const idToken = await otp.verifyOTP(otpInput);
    if (!idToken) return;
    try {
      const res = await axios.post(`${API}/auth/firebase`, {
        id_token: idToken,
        purpose: "reset_password",
      });
      setResetToken(res.data.reset_token);
      toast.success("OTP verified. Set your new password.");
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No account found for this mobile.");
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (password !== confirmPassword) return toast.error("Passwords do not match");
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/firebase/reset-password`, {
        reset_token: resetToken,
        new_password: password,
      });
      toast.success("Password updated. Please login.");
      navigate("/login");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password");
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <img src={LOGO_URL} alt="M Ventures" className="h-12 mx-auto mb-4" />
          </Link>
          <h1 className="font-heading text-2xl font-bold text-gray-900">Forgot Password</h1>
          <p className="text-gray-500">Reset your password with OTP</p>
        </div>

        <Card className="border-0 shadow-xl">
          <CardContent className="p-6 space-y-4">
            {step === 1 && (
              <>
                <div>
                  <Label>Mobile Number</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="tel"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value)}
                      maxLength={10}
                      placeholder="Enter 10-digit mobile"
                      className="pl-10"
                      data-testid="forgot-mobile-input"
                    />
                  </div>
                </div>
                <Button
                  className="w-full bg-secondary-500 !text-primary-900 hover:bg-secondary-400"
                  onClick={handleSendOTP}
                  disabled={otp.sending || mobile.length !== 10}
                  data-testid="forgot-send-otp-btn"
                >
                  {otp.sending ? "Sending..." : "Send OTP"}
                </Button>
              </>
            )}

            {step === 2 && (
              <>
                <div className="text-center text-gray-500">
                  OTP sent to +91{mobile}
                </div>
                <div>
                  <Label>Enter OTP</Label>
                  <Input
                    value={otpInput}
                    onChange={(e) => setOtpInput(e.target.value)}
                    maxLength={6}
                    placeholder="6-digit OTP"
                    className="text-center text-lg tracking-widest"
                    data-testid="forgot-otp-input"
                  />
                </div>
                <Button
                  className="w-full bg-secondary-500 !text-primary-900 hover:bg-secondary-400"
                  onClick={handleVerifyOTP}
                  disabled={otp.verifying || otpInput.length !== 6}
                  data-testid="forgot-verify-otp-btn"
                >
                  {otp.verifying ? "Verifying..." : "Verify OTP"}
                </Button>
                <button type="button" onClick={() => { otp.reset(); setStep(1); }} className="w-full text-sm text-primary-600">
                  Change mobile number
                </button>
              </>
            )}

            {step === 3 && (
              <form onSubmit={handleReset} className="space-y-4">
                <div>
                  <Label>New Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Min 6 characters"
                      className="pl-10 pr-10"
                      required
                      data-testid="forgot-new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label>Confirm New Password</Label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter password"
                    required
                    data-testid="forgot-confirm-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-secondary-500 !text-primary-900 hover:bg-secondary-400"
                  disabled={submitting}
                  data-testid="forgot-submit-btn"
                >
                  {submitting ? "Updating..." : "Set New Password"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Invisible reCAPTCHA */}
        <div id="recaptcha-container"></div>

        <div className="text-center mt-6">
          <Link to="/login" className="text-gray-500 hover:text-gray-700 inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
